import { ModifyQueryParamsAction } from '@liveauctioneers/caterwaul-components/lib/hooks/queryParams/useModifyQueryParams';
import { Warning } from '@liveauctioneers/hammer-ui-core/icons';
import { Tabs } from '@liveauctioneers/hammer-ui-core/tabs';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';

import CompleteAccountBanner from '@/components/CompleteAccountBanner/CompleteAccountBanner';
import PageTitle from '@/components/PageTitle/PageTitle';
import { useModifyQueryParams } from '@/hooks/queryParams/useModifyQueryParams';
import { useIsMobileLayout } from '@/hooks/useIsMobileLayout';
import { useAppSelector } from '@/redux/hooks';
import { getUiUpdateAccount } from '@/redux/modules/account/updateAccount/updateAccount';
import { getBaseUrl } from '@/redux/modules/config';
import { getIsReseller } from '@/redux/modules/reseller';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

import AccountSettingsPageHeaderMeta from './AccountSettingsPageHeaderMeta';
import useAccountSettingsPageTab from './hooks/useAccountSettingsPageTab';
import { PreferencesForm } from './PreferencesForm';
import { ProfileForm } from './ProfileForm';
import TaxCertManagementForm from './TaxCertManagementForm';
import { AccountSettingsPageTab } from './types/AccountSettings.types';
import UnsubscribeEmail from './UnsubscribeEmail';

export const AccountSettingsPage = () => {
    const isReseller = useAppSelector(getIsReseller);
    const formatMessage = useAppFormatMessage();
    const isMobile = useIsMobileLayout();
    const tab = useAccountSettingsPageTab();
    const baseUrl = useAppSelector(getBaseUrl);
    const { error: editProfileError, errorMessage } = useAppSelector(getUiUpdateAccount);

    const profileTitle = (
        <>
            <span className={cn({ hidden: isMobile })}>{formatMessage(['my_profile'])}</span>
            <span className={cn({ hidden: !isMobile })}>{formatMessage(['profile'])}</span>
        </>
    );

    const taxCertManagementTitle = <span>{formatMessage(['certManagement', 'taxes'])}</span>;

    const preferencesTitle = <span>{formatMessage(['preferences'])}</span>;

    const modifyQueryParams = useModifyQueryParams();

    const onTabChange = (name: string) => {
        if (name === AccountSettingsPageTab.Profile) {
            modifyQueryParams({
                action: ModifyQueryParamsAction.REMOVE,
                params: 'tab',
            });
        } else if (tab !== name) {
            modifyQueryParams({
                action: ModifyQueryParamsAction.ADD,
                query: { tab: name },
            });
        }
    };

    return (
        <div className="mx-auto mb-2xl w-full max-w-[1240px] px-md">
            <UnsubscribeEmail />
            <AccountSettingsPageHeaderMeta baseUrl={baseUrl} />
            <CompleteAccountBanner />
            {/* no hammer-ui token for beige100 (#fbf6f4) */}
            {editProfileError && (
                <div className="rounded-2xs my-sm flex w-full flex-row items-center self-center border border-border-secondary bg-[#fbf6f4] p-xs">
                    <Warning
                        className="mr-lg"
                        color="critical"
                        size="sm"
                        style="fill"
                    />
                    <p className="m-0">
                        {errorMessage
                            ? formatMessage(['account_update_custom_error'], {
                                  values: { error: errorMessage },
                              })
                            : formatMessage(['account_update_error'])}
                    </p>
                </div>
            )}
            <PageTitle
                className="my-md flex max-w-[1240px] flex-row self-center"
                title={formatMessage(['accountSettings', 'title'])}
            />
            <Tabs
                className={cn('mb-lg max-w-[1240px] self-center', {
                    'mb-md': isMobile,
                })}
                currentTab={tab}
                onTabChanged={onTabChange}
                tabs={[
                    {
                        content: <ProfileForm />,
                        contentId: AccountSettingsPageTab.Profile,
                        label: profileTitle,
                    },
                    ...(isReseller
                        ? [
                              {
                                  content: <TaxCertManagementForm />,
                                  contentId: AccountSettingsPageTab.TaxCertManagement,
                                  label: taxCertManagementTitle,
                              },
                          ]
                        : []),
                    {
                        content: <PreferencesForm />,
                        contentId: AccountSettingsPageTab.Preferences,
                        label: preferencesTitle,
                    },
                ]}
            />
        </div>
    );
};
